import React, { Suspense, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Grow,
  Typography,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { EventCardData } from '../events/eventCardTypes';
import { trackConversionEvent, trackEvent } from '../../utils/analytics';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';
import { HOMEPAGE_SECTION_PY } from '../../../shared/homepage/homepageLayout';
import { safeLazy } from '../../utils/safeLazy';
import {
  CONVERSION_ALERTS_CATEGORY,
  GEO_ALERT_CTA_CLICK_ACTION,
} from '../../../shared/analytics/conversionEvents';
import { dispatchTrainingAlertsNudge } from '../../../shared/conversion/trainingAlertsNudgeEvents';

const EventCard = safeLazy(() => import('../events/eventCardLazyEntry'), 'EventCard');

export type UpcomingTrainingSectionProps = {
  events: EventCardData[];
  loading?: boolean;
};

const UpcomingTrainingSection: React.FC<UpcomingTrainingSectionProps> = ({
  events,
  loading = false,
}) => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const nudgeDispatchedRef = useRef(false);

  useEffect(() => {
    if (!loading && events.length > 0) {
      void import('../events/eventCardLazyEntry').catch(() => {
        // Prefetch only — lazy boundary + ErrorBoundary handle visible failures.
      });
    }
  }, [loading, events.length]);

  useEffect(() => {
    if (nudgeDispatchedRef.current || loading) {
      return;
    }
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      return;
    }

    let engaged = false;
    let delayTimer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || entry.intersectionRatio < 0.35) {
          return;
        }
        if (engaged || nudgeDispatchedRef.current) {
          return;
        }
        engaged = true;
        // Delay so first paint / LCP is not competing with the nudge.
        delayTimer = window.setTimeout(() => {
          if (nudgeDispatchedRef.current) {
            return;
          }
          nudgeDispatchedRef.current = true;
          dispatchTrainingAlertsNudge({
            trigger: 'homepage_engagement',
            acceptMode: 'alerts',
            guestSource: 'nudge_homepage_engagement',
          });
        }, 2500);
      },
      { threshold: [0.35] },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (delayTimer != null) {
        window.clearTimeout(delayTimer);
      }
    };
  }, [loading, events.length]);

  const viewAll = () => {
    trackEvent({
      action: 'homepage_view_all_training_click',
      category: 'engagement',
      label: 'upcoming_training',
    });
    navigate('/events');
  };

  const handleAlertsClick = () => {
    trackConversionEvent({
      action: GEO_ALERT_CTA_CLICK_ACTION,
      category: CONVERSION_ALERTS_CATEGORY,
      label: 'homepage_upcoming_training',
      custom_parameters: {
        placement: 'homepage_upcoming_training',
      },
    });
  };

  return (
    <Box
      ref={sectionRef}
      sx={{ py: HOMEPAGE_SECTION_PY.upcoming, bgcolor: 'background.default', px: AGENCY_SHELL_HORIZONTAL_PX }}
    >
      <Container maxWidth="lg" disableGutters>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            mb: 1,
            textAlign: 'center',
          }}
        >
          Upcoming Training
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 2, maxWidth: 560, mx: 'auto' }}
        >
          Featured sessions starting in the next 30 days across the United States
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4, maxWidth: 520, mx: 'auto' }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            Get emailed when new training is posted near you.
          </Typography>
          <Button
            component={RouterLink}
            to="/alerts"
            variant="contained"
            size="medium"
            data-testid="homepage-training-alerts-cta"
            onClick={handleAlertsClick}
            sx={{ fontWeight: 600 }}
          >
            Get training alerts by email
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 420, md: 400 } }}>
            <CircularProgress aria-label="Loading upcoming training" />
          </Box>
        ) : events.length > 0 ? (
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: { xs: 420, md: 400 } }}>
                <CircularProgress aria-label="Loading event cards" />
              </Box>
            }
          >
            <Grid container spacing={3}>
              {events.slice(0, 3).map((event, index) => (
                <Grid item xs={12} md={4} key={event.id} sx={{ display: 'flex' }}>
                  <Grow in timeout={400 + index * 100}>
                    <div style={{ width: '100%' }}>
                      <EventCard event={event} showActions hideDescription />
                    </div>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          </Suspense>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No upcoming events right now
            </Typography>
            <Button variant="outlined" size="large" onClick={viewAll}>
              View All Training
              <ArrowForward sx={{ ml: 1 }} />
            </Button>
          </Box>
        )}

        {events.length > 0 && (
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              variant="contained"
              size="large"
              onClick={viewAll}
              sx={{ fontWeight: 700, px: 5, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
            >
              View All Training
              <ArrowForward sx={{ ml: 1.5 }} />
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default UpcomingTrainingSection;
